/* Headroom — the blog hub (sections/rm-hub.liquid). Net-new, not stock Concept.
   Everything here is an extra on top of a page that already works without JavaScript:
   - red lights on posts that are new since the reader's last visit
   - the next-review countdown
   - "How long have you got?" tabs
   - daily rotation (term of the day, vintage pick), so a cached page still turns over
   - hiding events that have already happened
   - "Surprise me": a random post from the whole archive, via sections/rm-hub-pick.liquid
   - "Continue reading": posts the reader started, saved by assets/rm-reading-progress.js
   Browser storage can be blocked or empty (private windows, cleared data); every read and write
   is guarded, and the page falls back to its server-rendered state. */

if (!customElements.get('rm-hub')) {
  const DAY_MS = 86400000;

  const storageArea = (type) => {
    try {
      return window[type] || null;
    } catch (error) {
      return null;
    }
  };

  const readStorage = (type, key) => {
    try {
      return storageArea(type)?.getItem(key) ?? null;
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (type, key, value) => {
    try {
      storageArea(type)?.setItem(key, value);
    } catch (error) {
      // Storage is blocked or full; the hub works without it.
    }
  };

  class RmHub extends HTMLElement {
    connectedCallback() {
      // The theme editor re-renders the section into a new element, which runs this again.
      if (this.initialized) return;
      this.initialized = true;

      this.initEvents();
      this.initLights();
      this.initCountdowns();
      this.initTabs();
      this.initRotators();
      this.initSurprise();
      this.initContinue();
    }

    // Removes a tile, and its column when that leaves the column empty, so the row closes up.
    removeTile(tile) {
      if (!tile) return;
      const column = tile.parentElement;
      tile.remove();
      if (column?.classList.contains('rm-hub__col') && !column.children.length) column.remove();
    }

    lightTile(element) {
      element.closest('.rm-hub-tile')?.querySelector('[data-tile-light]')?.classList.add('is-on');
    }

    /* The baseline is the previous visit's time, fixed for this browser session, so the lights
       survive a refresh. The stored last visit moves on to now straight away. */
    initLights() {
      if (this.dataset.lights !== 'true') return;

      const now = Date.now();
      let baseline = Number(readStorage('sessionStorage', 'rm-hub-baseline'));
      if (!baseline) {
        baseline = Number(readStorage('localStorage', 'rm-hub-last-visit')) || now;
        writeStorage('sessionStorage', 'rm-hub-baseline', String(baseline));
      }
      writeStorage('localStorage', 'rm-hub-last-visit', String(now));
      if (baseline >= now) return; // first visit: nothing to compare against

      const fresh = new Set();
      this.querySelectorAll('[data-published]').forEach((element) => {
        if (Number(element.dataset.published) * 1000 <= baseline) return;
        fresh.add(element.getAttribute('href'));
        element.querySelectorAll('[data-hub-light]').forEach((light) => {
          light.hidden = false;
        });
        this.lightTile(element);
      });

      const note = this.querySelector('[data-hub-new]');
      if (!note || !fresh.size) return;
      note.querySelector('[data-hub-new-text]').textContent = `${fresh.size} new since your last visit`;
      note.hidden = false;
    }

    initCountdowns() {
      this.querySelectorAll('[data-hub-countdown]').forEach((display) => {
        const weekday = Number(display.dataset.weekday);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysAway = (weekday - today.getDay() + 7) % 7;
        const next = new Date(today);
        next.setDate(today.getDate() + daysAway);

        const day = next
          .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
          .replace(',', '');
        let when = `In ${daysAway} days`;
        if (daysAway === 0) when = 'Out today';
        if (daysAway === 1) when = 'Tomorrow';

        display.querySelector('[data-hub-countdown-day]').textContent = day;
        display.querySelector('[data-hub-countdown-when]').textContent = when;
        if (daysAway === 0) this.lightTile(display);
      });
    }

    initTabs() {
      this.querySelectorAll('[data-hub-tabs]').forEach((tablist) => {
        const tabs = [...tablist.querySelectorAll('[role="tab"]')];

        const select = (tab, moveFocus) => {
          tabs.forEach((candidate) => {
            const selected = candidate === tab;
            candidate.setAttribute('aria-selected', String(selected));
            candidate.tabIndex = selected ? 0 : -1;
            const panel = document.getElementById(candidate.getAttribute('aria-controls'));
            if (panel) panel.hidden = !selected;
          });
          if (moveFocus) tab.focus();
        };

        tablist.addEventListener('click', (event) => {
          const tab = event.target.closest('[role="tab"]');
          if (tab) select(tab, false);
        });

        tablist.addEventListener('keydown', (event) => {
          const index = tabs.indexOf(document.activeElement);
          if (index < 0) return;
          const targets = {
            ArrowRight: tabs[(index + 1) % tabs.length],
            ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
            Home: tabs[0],
            End: tabs[tabs.length - 1],
          };
          const target = targets[event.key];
          if (!target) return;
          event.preventDefault();
          select(target, true);
        });
      });
    }

    // Same formula as the Liquid (days since 1970, modulo the count), so it only changes a cached page.
    initRotators() {
      const today = Math.floor(Date.now() / DAY_MS);
      this.querySelectorAll('[data-hub-rotate]').forEach((group) => {
        const items = [...group.children].filter((child) => child.hasAttribute('data-hub-rotate-item'));
        if (items.length < 2) return;
        const pick = today % items.length;
        items.forEach((item, index) => {
          item.hidden = index !== pick;
        });
      });
    }

    initEvents() {
      const now = Date.now() / 1000;
      this.querySelectorAll('[data-hub-events]').forEach((list) => {
        list.querySelectorAll('[data-hub-ends]').forEach((event) => {
          if (Number(event.dataset.hubEnds) < now) event.remove();
        });
        if (!list.children.length) this.removeTile(list.closest('.rm-hub-tile'));
      });
    }

    initSurprise() {
      this.querySelectorAll('[data-hub-surprise]').forEach((container) => {
        let archive = [];
        try {
          archive = JSON.parse(container.querySelector('[data-hub-archive]').textContent);
        } catch (error) {
          return;
        }
        archive = archive.filter((source) => source.count > 0);
        const total = archive.reduce((sum, source) => sum + source.count, 0);
        const button = container.querySelector('[data-hub-stomp]');
        const slot = container.querySelector('[data-hub-pick-slot]');
        const light = container.closest('.rm-hub-tile')?.querySelector('[data-tile-light]');
        if (!button || !slot || !total) return;

        const fetchPick = async () => {
          let position = Math.floor(Math.random() * total);
          const source = archive.find((candidate) => {
            if (position < candidate.count) return true;
            position -= candidate.count;
            return false;
          });
          const url = `${source.url}?section_id=rm-hub-pick&page=${position + 1}`;
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Pick request failed: ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = html.querySelector('[data-hub-pick]');
          if (!card) throw new Error('Pick response had no card');
          const label = card.querySelector('[data-pick-label]');
          if (label && source.label) label.textContent = source.label;
          return card;
        };

        button.addEventListener('click', async () => {
          if (button.getAttribute('aria-busy') === 'true') return;
          button.setAttribute('aria-busy', 'true');
          light?.classList.add('is-on');
          const current = slot.querySelector('[data-hub-pick]')?.getAttribute('href');
          try {
            // Try again if the random pick is the post already showing.
            for (let attempt = 0; attempt < 3; attempt += 1) {
              const card = await fetchPick();
              if (card.getAttribute('href') !== current || attempt === 2) {
                slot.replaceChildren(document.importNode(card, true));
                break;
              }
            }
          } catch (error) {
            // Keep the current pick.
          } finally {
            button.removeAttribute('aria-busy');
            setTimeout(() => light?.classList.remove('is-on'), 600);
          }
        });
      });
    }

    initContinue() {
      this.querySelectorAll('[data-hub-continue]').forEach((container) => {
        const tile = container.closest('.rm-hub-tile');
        const fallback = container.querySelector('[data-hub-continue-fallback]');

        let entries = [];
        try {
          entries = JSON.parse(readStorage('localStorage', 'rm-reading') || '[]');
        } catch (error) {
          entries = [];
        }
        if (!Array.isArray(entries)) entries = [];
        entries = entries
          .filter((entry) => entry && typeof entry.u === 'string' && entry.u.startsWith('/') && entry.p >= 5 && entry.p < 90)
          .sort((a, b) => b.d - a.d)
          .slice(0, Number(container.dataset.count) || 2);

        if (!entries.length) {
          if (!fallback?.querySelector('li')) this.removeTile(tile);
          return;
        }

        const list = container.querySelector('[data-hub-continue-list]');
        const template = container.querySelector('[data-hub-continue-template]');
        if (!list || !template) return;

        entries.forEach((entry) => {
          const row = template.content.firstElementChild.cloneNode(true);
          row.querySelector('a').href = entry.u;
          row.querySelector('[data-title]').textContent = entry.t || entry.u;
          const image = row.querySelector('img');
          let imageUrl = null;
          try {
            imageUrl = entry.i ? new URL(entry.i, window.location.href) : null;
          } catch (error) {
            imageUrl = null;
          }
          if (imageUrl && ['https:', 'http:'].includes(imageUrl.protocol)) image.src = imageUrl.href;
          else image.remove();
          const percent = Math.round(entry.p);
          row.querySelector('[data-bar]').style.width = `${percent}%`;
          const minutesLeft = Math.max(1, Math.ceil((Number(entry.m) || 0) * (1 - entry.p / 100)));
          row.querySelector('[data-left]').textContent = entry.m ? `${minutesLeft} min left` : `${percent}% read`;
          list.append(row);
        });

        list.hidden = false;
        if (fallback) fallback.hidden = true;
        const name = tile?.querySelector('[data-hub-tile-name]');
        if (name && container.dataset.title) name.textContent = container.dataset.title;
      });
    }
  }

  customElements.define('rm-hub', RmHub);
}
